import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  FileText, 
  Search, 
  Eye, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Calendar,
  User,
  MapPin,
  Tag,
  Send,
  X,
  Filter,
  ChevronDown,
  Trash2,
  MessageSquare
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { usePlaintes, STATUTS_PLAINTE } from '../../hooks/usePlaintes';

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-light);
  }

  input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.75rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }
  }
`;

const FilterSelect = styled.select`
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  background: var(--white);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    width: 48px;
    height: 48px;
    background: ${props => props.$bg || 'linear-gradient(135deg, var(--primary-blue), #004d8f)'};
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
  }

  .content {
    .value {
      font-size: 1.5rem;
      font-weight: 800;
      color: var(--text-dark);
      font-family: var(--font-secondary);
    }

    .label {
      font-size: 0.85rem;
      color: var(--text-light);
    }
  }
`;

const Table = styled.table`
  width: 100%;
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem 1.25rem;
  background: var(--off-white);
  font-weight: 600;
  color: var(--text-dark);
  font-size: 0.9rem;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-light);
  font-size: 0.95rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.875rem;
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: 0.85rem;
  background: ${props => props.$bg};
  color: ${props => props.$color};
  white-space: nowrap;
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: var(--radius-md);
  color: var(--text-medium);
  cursor: pointer;
  transition: var(--transition);
  margin-right: 0.5rem;

  &:hover {
    background: var(--background-light);
    color: var(--primary-blue);
  }

  &.delete:hover {
    background: #FEE2E2;
    color: #DC2626;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 2px solid var(--border-light);
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  button {
    color: var(--white);
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  .label {
    font-size: 0.85rem;
    color: var(--text-light);
    margin-bottom: 0.25rem;
  }

  .value {
    font-weight: 600;
    color: var(--text-dark);
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.1rem;
    color: var(--text-dark);
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      color: var(--primary-blue);
    }
  }
`;

const ContentBox = styled.div`
  background: var(--background-light);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  line-height: 1.7;
  color: var(--text-medium);
`;

const StatusSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  width: 100%;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &.primary {
    background: var(--primary-blue);
    color: var(--white);

    &:hover {
      background: #004d8f;
    }
  }

  &.secondary {
    background: var(--background-light);
    color: var(--text-dark);

    &:hover {
      background: var(--border-light);
    }
  }
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 1.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: var(--border-light);
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 1.25rem;

  &::before {
    content: '';
    position: absolute;
    left: -1.25rem;
    top: 0.25rem;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.$color || 'var(--primary-blue)'};
  }

  .date {
    font-size: 0.8rem;
    color: var(--text-light);
  }

  .title {
    font-weight: 600;
    font-size: 0.95rem;
  }

  .comment {
    font-size: 0.9rem;
    color: var(--text-medium);
    margin-top: 0.25rem;
  }
`;

const AdminPlaintes = () => {
  const { plaintes, loading, stats, updateStatut, addReponse, deletePlainte } = usePlaintes();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPlainte, setSelectedPlainte] = useState(null);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [responseForm, setResponseForm] = useState({
    statut: '',
    commentaire: '',
    reponse: ''
  });

  // Filtrer les plaintes
  const filteredPlaintes = plaintes.filter(p => {
    const matchesSearch = 
      p.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.objet.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || p.statut === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleOpenResponse = (plainte) => {
    setSelectedPlainte(plainte);
    setResponseForm({
      statut: plainte.statut,
      commentaire: '',
      reponse: plainte.reponse || ''
    });
    setIsResponseModalOpen(true);
  };

  const handleSubmitResponse = async () => {
    if (!selectedPlainte) return;

    // Mettre à jour le statut si changé
    if (responseForm.statut !== selectedPlainte.statut) {
      const result = await updateStatut(
        selectedPlainte.id, 
        responseForm.statut, 
        responseForm.commentaire
      );
      if (!result.success) {
        alert('Erreur: ' + result.error);
        return;
      }
    }

    // Ajouter la réponse
    if (responseForm.reponse) {
      const result = await addReponse(selectedPlainte.id, responseForm.reponse);
      if (!result.success) {
        alert('Erreur: ' + result.error);
        return;
      }
    }

    setIsResponseModalOpen(false);
    setSelectedPlainte(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette Réclamation ')) {
      const result = await deletePlainte(id);
      if (!result.success) {
        alert('Erreur: ' + result.error);
      }
    }
  };

  return (
    (<AdminLayout title='Gestion des Réclamations'>
      <HeaderActions>
        <SearchBox>
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher par référence, nom ou objet..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="nouvelle">Nouvelles</option>
          <option value="en_cours">En cours</option>
          <option value="traitee">Traitées</option>
          <option value="classee">Classées</option>
        </FilterSelect>
      </HeaderActions>
      <StatsGrid>
        <StatCard>
          <div className="icon">
            <FileText size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.total}</div>
            <div className="label">Total Réclamations</div>
          </div>
        </StatCard>
        <StatCard $bg="linear-gradient(135deg, #3B82F6, #60A5FA)">
          <div className="icon">
            <AlertCircle size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.nouvelles}</div>
            <div className="label">Nouvelles</div>
          </div>
        </StatCard>
        <StatCard $bg="linear-gradient(135deg, #F59E0B, #FBBF24)">
          <div className="icon">
            <Clock size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.enCours}</div>
            <div className="label">En cours</div>
          </div>
        </StatCard>
        <StatCard $bg="linear-gradient(135deg, #10B981, #34D399)">
          <div className="icon">
            <CheckCircle size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.traitees}</div>
            <div className="label">Traitées</div>
          </div>
        </StatCard>
        <StatCard $bg="linear-gradient(135deg, #6B7280, #9CA3AF)">
          <div className="icon">
            <XCircle size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.classees}</div>
            <div className="label">Classées</div>
          </div>
        </StatCard>
      </StatsGrid>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>Chargement...</div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Référence</Th>
              <Th>Date</Th>
              <Th>Déposant</Th>
              <Th>Objet</Th>
              <Th>Statut</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredPlaintes.length === 0 ? (
              <tr>
                <Td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                                  Aucune Réclamation trouvée
                                </Td>
              </tr>
            ) : (
              filteredPlaintes.map((plainte) => (
                <tr key={plainte.id}>
                  <Td>
                    <strong style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>
                      {plainte.reference}
                    </strong>
                  </Td>
                  <Td>
                    {plainte.date.toLocaleDateString('fr-FR')}
                  </Td>
                  <Td>
                    {plainte.prenom} {plainte.nom}
                  </Td>
                  <Td>{plainte.objet}</Td>
                  <Td>
                    <StatusBadge 
                      $bg={STATUTS_PLAINTE[plainte.statut].bg} 
                      $color={STATUTS_PLAINTE[plainte.statut].color}
                    >
                      {STATUTS_PLAINTE[plainte.statut].label}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <ActionButton onClick={() => handleOpenResponse(plainte)} title="Voir / Répondre">
                      <Eye size={18} />
                    </ActionButton>
                    <ActionButton 
                      className="delete" 
                      onClick={() => handleDelete(plainte.id)}
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </ActionButton>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
      {/* Modal de réponse */}
      {isResponseModalOpen && selectedPlainte && (
        <ModalOverlay onClick={() => setIsResponseModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>
                <FileText size={24} />
                              RéRéclamation {selectedPlainte.reference}
              </h2>
              <button onClick={() => setIsResponseModalOpen(false)}>
                <X size={24} />
              </button>
            </ModalHeader>

            <ModalBody>
              <InfoGrid>
                <InfoItem>
                  <div className="label">Déposée par</div>
                  <div className="value">{selectedPlainte.prenom} {selectedPlainte.nom}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Email</div>
                  <div className="value">{selectedPlainte.email || 'Non renseigné'}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Localité</div>
                  <div className="value">{selectedPlainte.localite}</div>
                </InfoItem>
              </InfoGrid>

              <Section>
                <h3><Tag size={20} /> Type et Objet</h3>
                <ContentBox>
                  <strong>Type:</strong> {selectedPlainte.type}<br />
                  <strong>Objet:</strong> {selectedPlainte.objet}
                </ContentBox>
              </Section>

              <Section>
                <h3><FileText size={20} /> Description</h3>
                <ContentBox>{selectedPlainte.description}</ContentBox>
              </Section>

              <Section>
                <h3><Clock size={20} /> Changement de statut</h3>
                <StatusSelect
                  value={responseForm.statut}
                  onChange={(e) => setResponseForm({ ...responseForm, statut: e.target.value })}
                >
                  <option value="nouvelle">Nouvelle</option>
                  <option value="en_cours">En cours de traitement</option>
                  <option value="traitee">Traitée</option>
                  <option value="classee">Classée sans suite</option>
                </StatusSelect>
                
                {responseForm.statut !== selectedPlainte.statut && (
                  <TextArea
                    placeholder="Commentaire sur le changement de statut (optionnel)"
                    value={responseForm.commentaire}
                    onChange={(e) => setResponseForm({ ...responseForm, commentaire: e.target.value })}
                  />
                )}
              </Section>

              <Section>
                <h3><MessageSquare size={20} /> Réponse au plaignant</h3>
                <TextArea
                  placeholder="Rédigez votre réponse officielle ici..."
                  value={responseForm.reponse}
                  onChange={(e) => setResponseForm({ ...responseForm, reponse: e.target.value })}
                />
              </Section>

              {selectedPlainte.historique.length > 0 && (
                <Section>
                  <h3><Clock size={20} /> Historique</h3>
                  <Timeline>
                    {selectedPlainte.historique.map((item, index) => (
                      <TimelineItem 
                        key={index}
                        $color={STATUTS_PLAINTE[item.statut].color}
                      >
                        <div className="date">
                          {new Date(item.date).toLocaleString('fr-FR')}
                        </div>
                        <div className="title">
                          {STATUTS_PLAINTE[item.statut].label || item.statut}
                        </div>
                        <div className="comment">{item.commentaire}</div>
                      </TimelineItem>
                    ))}
                  </Timeline>
                </Section>
              )}

              <ButtonGroup>
                <Button className="secondary" onClick={() => setIsResponseModalOpen(false)}>
                  Annuler
                </Button>
                <Button className="primary" onClick={handleSubmitResponse}>
                  <Send size={18} />
                  Enregistrer
                </Button>
              </ButtonGroup>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </AdminLayout>)
  );
};

export default AdminPlaintes;
